import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, History } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { api, errMsg } from "../lib/api";
import { money, pct } from "../lib/format";
import { Panel, Empty } from "./Widgets";
import { Input } from "./ui/input";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function YearComparison({ trend, year }) {
  const prev = String(Number(year) - 1);
  const by = Object.fromEntries(trend.map((t) => [t.month, t.payout]));
  const data = MONTHS.map((m, i) => {
    const mm = String(i + 1).padStart(2, "0");
    return { label: m, [year]: by[`${year}-${mm}`] ?? null, [prev]: by[`${prev}-${mm}`] ?? null };
  });
  const sum = (y) => trend.filter((t) => t.month.startsWith(y)).reduce((s, t) => s + t.payout, 0);
  const now = sum(year), before = sum(prev);
  return (
    <Panel title={`${year} vs ${prev}`} className="mb-6" action={
      <span className="text-xs font-mono text-slate-400" data-testid="year-compare-totals">{money(now)} <span className="text-slate-600">vs</span> {money(before)}</span>}>
      <div className="p-4 h-64" data-testid="year-comparison">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ left: 10, right: 10, top: 10 }}>
            <CartesianGrid stroke="#1E293B" vertical={false} />
            <XAxis dataKey="label" stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} />
            <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => (Math.abs(v) >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${Math.round(v)}`)} />
            <Tooltip cursor={{ fill: "#1E293B" }} labelStyle={{ color: "#F1F5F9" }} itemStyle={{ color: "#E2E8F0" }} contentStyle={{ background: "#131B2E", border: "1px solid #1E293B", borderRadius: 6, fontFamily: "JetBrains Mono", fontSize: 12 }} formatter={(v) => (v == null ? "—" : money(v))} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey={prev} fill="#475569" radius={[3, 3, 0, 0]} />
            <Bar dataKey={year} fill="#10B981" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Panel>
  );
}

const LABELS = { name: "Name", email: "Email", agent_code: "Agent code", split_pct: "Split", fee_per_merchant: "Fee / merchant", bonus_split_pct: "Bonus split", bonus_min_new: "New accounts needed" };
const fmt = (k, v) => (v == null || v === "" ? "—" : k.includes("pct") ? pct(v) : k === "fee_per_merchant" ? money(v) : String(v));

export function ChangeLog({ agentId }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get(`/agents/${agentId}/history`).then((r) => setItems(r.data)); }, [agentId]);
  if (!items) return null;
  return (
    <Panel title="Split & fee history" className="mt-6" action={<History size={15} className="text-slate-500" />}>
      {items.length === 0 ? <Empty>No changes recorded yet. Future edits to splits, fees and codes will be logged here.</Empty> : (
        <div className="divide-y divide-slate-800" data-testid="change-log">
          {items.map((c) => (
            <div key={c.id} className="px-5 py-3" data-testid={`change-${c.id}`}>
              <div className="flex justify-between text-xs font-mono text-slate-500 mb-1">
                <span className="uppercase text-slate-300">{c.action}</span><span>{new Date(c.at).toLocaleString()} · {c.by}</span>
              </div>
              {Object.entries(c.changes).filter(([k]) => c.action !== "created" || c.changes[k][1] != null).map(([k, [a, b]]) => (
                <div key={k} className="text-sm"><span className="text-slate-400">{LABELS[k] || k}:</span>{" "}
                  {c.action === "created" ? <span className="font-mono">{fmt(k, b)}</span>
                    : <><span className="font-mono text-rose-300 line-through">{fmt(k, a)}</span> → <span className="font-mono text-emerald-400">{fmt(k, b)}</span></>}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

export function TagChips({ tags, onRemove }) {
  if (!tags?.length) return null;
  return (
    <span className="inline-flex flex-wrap gap-1 ml-2 align-middle">
      {tags.map((t) => (
        <span key={t} data-testid={`tag-chip-${t}`} className="inline-flex items-center gap-1 text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-300 border border-blue-500/30">
          {t}{onRemove && <button data-testid={`remove-tag-${t}`} onClick={() => onRemove(t)} className="hover:text-rose-400"><X size={10} /></button>}
        </span>
      ))}
    </span>
  );
}

export function TagEditor({ merchantId, onChange }) {
  const [tags, setTags] = useState([]);
  const [all, setAll] = useState([]);
  const [text, setText] = useState("");
  useEffect(() => {
    api.get("/tags/map").then((r) => setTags(r.data[merchantId] || []));
    api.get("/tags").then((r) => setAll(r.data));
  }, [merchantId]);

  const save = async (next) => {
    try {
      const { data } = await api.put(`/merchants/${merchantId}/tags`, { tags: next });
      setTags(data.tags); onChange?.();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const add = (t) => { if (t.trim()) save([...tags, t.trim()]); setText(""); };
  const suggestions = all.map((a) => a.tag).filter((t) => !tags.includes(t));

  return (
    <div data-testid="tag-editor">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-slate-500">Tags:</span>
        {tags.length ? <TagChips tags={tags} onRemove={(t) => save(tags.filter((x) => x !== t))} /> : <span className="text-xs text-slate-600">none</span>}
      </div>
      <div className="flex gap-2 mt-2 items-center flex-wrap">
        <Input data-testid="tag-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add(text)}
          placeholder="Add tag, e.g. VIP" className="h-8 w-48 bg-[#0B0F17] text-xs" />
        {["VIP", "High risk", "Pending close"].concat(suggestions).filter((t, i, arr) => arr.indexOf(t) === i && !tags.includes(t)).slice(0, 6).map((t) => (
          <button key={t} data-testid={`suggest-tag-${t}`} onClick={() => add(t)} className="text-[11px] px-2 py-1 rounded border border-slate-700 text-slate-400 hover:text-blue-300 hover:border-blue-500/40">+ {t}</button>
        ))}
      </div>
    </div>
  );
}
